import type { TableTableRun } from './state';
import { computed, createModel } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal } from '@preact/signals-react';
import type { Stage } from '@thinkalpha/platform-ws-client/contracts/table.js';
import { stringifyKey } from '@thinkalpha/table-client';
import type { Key, SortModel } from '@thinkalpha/table-client';
import { createTable } from 'src/lib/table';
import { makeOptimizedTableFilterKey, makeOptimizedTableSortKey } from 'src/models/TableModel';
import type { TableCreationQuery, TableModel } from 'src/models/TableModel';
import type { FullKeyType } from 'src/types';

export interface TableQueryModelInit {
    readonly run: ReadonlySignal<TableTableRun | null>;
    readonly table: TableModel;
    readonly userViewKey: string;
}

export interface TableQueryModelShape {
    readonly queryOptions: ReadonlySignal<TableCreationQuery>;
}

export function makeTableQueryOptions(
    tableKey: Key | null,
    sort: SortModel | null | undefined,
    filter: string | undefined,
    userViewKey: string,
): TableCreationQuery {
    const stringTableKey = stringifyKey(tableKey);
    const sortModel = sort ?? {};
    return {
        enabled: stringTableKey !== null,
        queryKey: [
            'table-widget',
            stringTableKey,
            userViewKey,
            makeOptimizedTableSortKey(sortModel),
            makeOptimizedTableFilterKey(filter, 'raw-formula'),
        ],
        queryFn: async (_context, debugIdentifier?: string) => {
            if (stringTableKey === null) return undefined;

            const inputStage: Stage = {
                type: 'legacy-table',
                tableKey: stringTableKey,
                rowKey: 'todo-maybe',
            };
            const leafStage: Stage = {
                type: 'output',
                columns: undefined,
                inputStage,
                sort: sortModel,
                filter: filter === undefined ? undefined : { type: 'raw-formula', value: filter },
            };
            const result = await createTable({ leafStage, userViewKey }, undefined, debugIdentifier);
            return { ...result, isScrolling: tableKey?.ex === 'S', fetchMode: undefined } satisfies FullKeyType;
        },
        staleTime: 0,
    };
}

export const TableQueryModel: ModelConstructor<TableQueryModelShape, [TableQueryModelInit]> = createModel<
    TableQueryModelShape,
    [TableQueryModelInit]
>((init: TableQueryModelInit) => {
    const queryOptions = computed(() => {
        const run = init.run.value;
        return makeTableQueryOptions(
            run?.tableKey ?? null,
            run?.sort,
            run?.filter,
            run?.userViewKey ?? init.userViewKey,
        );
    });

    init.table.init({
        queryOptions,
        initBounds: { firstRow: 0, windowSize: 100 },
        devtoolsName: 'TableWidget/table',
    });

    return { queryOptions };
});

export type TableQueryModel = Model<TableQueryModelShape>;
